import { GraviGrid, Horizontal, Vertical } from '@gravitate-js/excalibrr'
import type { IFilter } from 'ag-grid-community'
import { useMileageBandDetails } from '../hooks'
import { useMileageBandColumnDefs } from './useMileageBandColumnDefs'
import { RatebookHeader } from './RatebookHeader'
import { RateScheduleTabs } from './RateScheduleTabs'
import { EditableMileageBandsGrid } from './EditableMileageBandsGrid'
import { EditModeFooter } from './EditModeFooter'
import type { MileageBand, MileageBandDetailsProps } from '../types'

/**
 * MileageBandDetails component displays and manages a ratebook's mileage band details
 * It shows the rate schedule history and allows editing, creating, and deleting rates
 */
export function MileageBandDetails({ selectedGroup, ratebook, isEditMode, setIsEditMode }: MileageBandDetailsProps) {
  // Use the custom hook to manage state and logic
  const {
    isCreatePopoverOpen,
    setIsCreatePopoverOpen,
    saveLoading,
    selectedRate,
    setSelectedRate,
    dirtyGridApiRef,
    editingCopyRows,
    setEditingCopyRows,
    isChangeEffectivePopoverOpen,
    setIsChangeEffectivePopoverOpen,
    hasDuplicateMiles,
    setHasDuplicateMiles,
    hasInvalidMileValues,
    setHasInvalidMileValues,
    sortedHistory,
    handleCreateRate,
    handleDeleteRate,
    saveRateChanges,
    handleUpdateEffectiveDate,
    handleCancelEdit,
  } = useMileageBandDetails({
    selectedGroup,
    ratebook,
    isEditMode,
    setIsEditMode,
  })

  // Get column definitions for the mileage band grid
  const mileageBandColumnDefs = useMileageBandColumnDefs(selectedRate)

  return (
    <Vertical flex={1}>
      <RatebookHeader
        selectedGroup={selectedGroup}
        isEditMode={isEditMode}
        isCreatePopoverOpen={isCreatePopoverOpen}
        setIsCreatePopoverOpen={setIsCreatePopoverOpen}
        sortedHistory={sortedHistory}
        handleCreateRate={handleCreateRate}
      />

      <Vertical flex={1}>
        {isEditMode ? (
          <EditableMileageBandsGrid
            mileageBands={selectedRate?.rates || []}
            rateType={selectedRate?.unit || ''}
            dirtyGridApiRef={dirtyGridApiRef}
            saveRateChanges={saveRateChanges}
            editingCopyRows={editingCopyRows}
            setEditingCopyRows={setEditingCopyRows}
            onHasDuplicatesChange={setHasDuplicateMiles}
            onHasInvalidMileValuesChange={setHasInvalidMileValues}
          />
        ) : (
          <Horizontal flex={1} gap={8} style={{ width: '100%' }}>
            <RateScheduleTabs
              sortedHistory={sortedHistory}
              selectedRate={selectedRate}
              setSelectedRate={setSelectedRate}
              setIsEditMode={setIsEditMode}
              handleDeleteRate={handleDeleteRate}
              isChangeEffectivePopoverOpen={isChangeEffectivePopoverOpen}
              setIsChangeEffectivePopoverOpen={setIsChangeEffectivePopoverOpen}
              handleUpdateEffectiveDate={handleUpdateEffectiveDate}
              saveLoading={saveLoading}
            />
            <Vertical flex={1}>
              {selectedRate && (
                <GraviGrid<MileageBand, IFilter>
                  rowData={selectedRate?.rates || []}
                  columnDefs={mileageBandColumnDefs}
                  agPropOverrides={{
                    getRowId: (params) => `${params.data.from_mile}-${params.data.to_mile}`,
                    rowGroupPanelShow: 'never',
                    rowHeight: 40,
                    singleClickEdit: true,
                  }}
                  storageKey='ratebook-mileage-bands'
                  controlBarProps={{
                    title: 'Mileage Bands',
                    customSearchBar: <div />,
                  }}
                />
              )}
            </Vertical>
          </Horizontal>
        )}
      </Vertical>

      {isEditMode && selectedRate && (
        <EditModeFooter
          selectedRate={selectedRate}
          handleCancelEdit={handleCancelEdit}
          saveRateChanges={saveRateChanges}
          hasDuplicateMiles={hasDuplicateMiles}
          hasInvalidMileValues={hasInvalidMileValues}
          saveLoading={saveLoading}
          editingCopyRows={editingCopyRows}
        />
      )}
    </Vertical>
  )
}
